import React from "react";
import { toPng } from "html-to-image";
import Wordmark from "./Wordmark";
import TasteSignal from "./TasteSignal";

// SHAREABLE SIGNAL CARD — a clean, premium, screenshot/post-worthy snapshot of
// the five-signal Taste fingerprint. Portrait 9:16-friendly composition:
// TASTE TEST branding · "Your Taste Signal" · the five spectra · one profile
// line. No CTAs, no nav chrome inside the captured artifact. Share-safe: it
// contains NO poster imagery, so no export-rights are required. Users can
// Save/Share it as a PNG, or simply screenshot it.

export default function SignalCard({ profile, onBack }) {
  const rows = profile?.tasteSignal || [];
  const heading =
    !profile?.scopeLabel || profile?.scope === "CROSS-MEDIA"
      ? "Your Taste Signal"
      : `Your ${profile.scopeLabel} Taste Signal`;

  const cardRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState(null); // 'saved' | 'shared' | 'error'

  const capture = React.useCallback(async () => {
    if (!cardRef.current) return null;
    // Two passes improves web-font embedding reliability in the first export.
    await toPng(cardRef.current, { cacheBust: true, backgroundColor: "#08090d" });
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#08090d",
    });
  }, []);

  const handleShare = React.useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const dataUrl = await capture();
      if (!dataUrl) throw new Error("no-capture");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "taste-signal.png", { type: "image/png" });

      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: "My Taste Signal",
            text: "My Taste Signal — TASTE TEST · TasteTestNow.com",
          });
          setStatus("shared");
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return; // user cancelled; not an error
          // otherwise fall through to download
        }
      }

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "taste-signal.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setStatus("saved");
    } catch (e) {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }, [busy, capture]);

  return (
    <div className="relative min-h-[100dvh] w-full flex flex-col items-center justify-center bg-canvas px-5 py-8">
      {/* The captured artifact */}
      <div
        ref={cardRef}
        data-testid="signal-card"
        className="relative w-full max-w-[400px] rounded-3xl overflow-hidden border border-white/10 flex flex-col px-6 pt-9 pb-8"
        style={{
          background:
            "radial-gradient(120% 70% at 50% 0%, rgba(255,42,85,0.10), transparent 55%), radial-gradient(120% 70% at 50% 100%, rgba(0,229,255,0.10), transparent 55%), #08090d",
        }}
      >
        <div className="flex items-center justify-center">
          <Wordmark variant="stacked" className="h-16" />
        </div>

        <div
          className="mt-6 h-px w-full"
          style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))", opacity: 0.6 }}
        />

        <p className="mt-6 text-center font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle">
          {heading}
        </p>

        <div className="mt-5">
          <TasteSignal rows={rows} heading="" />
        </div>

        {profile?.oneLine && (
          <p className="mt-6 text-center font-display font-bold text-bone text-[15px] leading-snug tracking-[-0.01em]">
            {profile.oneLine}
          </p>
        )}

        <p className="mt-7 text-center font-mono text-[9px] tracking-[0.3em] uppercase text-subtle/70">
          TasteTestNow.com
        </p>
      </div>

      {/* Actions — outside the captured node */}
      <div className="mt-7 w-full max-w-[400px] flex flex-col items-center gap-3">
        <button
          type="button"
          data-testid="signalcard-share"
          onClick={handleShare}
          disabled={busy}
          className="group relative w-full rounded-full py-3.5 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a] overflow-hidden active:scale-[0.99] transition-transform disabled:opacity-60"
          style={{ background: "linear-gradient(90deg, var(--tt-left), var(--tt-right))" }}
        >
          {busy ? "Preparing…" : "Save / share your card"}
        </button>

        <p
          className="h-4 font-mono text-[9.5px] tracking-[0.16em] uppercase text-subtle"
          data-testid="signalcard-status"
          aria-live="polite"
        >
          {status === "saved" && "Saved to your device"}
          {status === "shared" && "Shared"}
          {status === "error" && "Couldn't export — screenshot to share"}
        </p>

        {onBack && (
          <button
            type="button"
            data-testid="signalcard-back"
            onClick={onBack}
            className="font-body font-medium text-[11px] tracking-[0.2em] uppercase text-white/45 hover:text-white/75 transition-colors"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}
